import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import { newsHubFields } from 'common/constants/query-fields/news';
import { hasAdminAccess } from 'common/utils/user';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import newsSlice from 'store/modules/news';

const PER_PAGE = 9;

const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const cardShellSx = {
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'hairline.strong',
  backgroundColor: 'surface.content',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms cubic-bezier(0.32,0.72,0,1)',
  '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' },
};

/**
 * Hub de noticias estilo blog (ref. MySchoolUAE): cards con imagen, fecha, título,
 * resumen y "Leer más", con toggle grid/lista y contador de artículos. El ADMIN ve
 * las mismas cards con acciones de editar/eliminar (reemplaza su tabla CRUD).
 */
function NewsHubPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['news', 'routes', 'buttons']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const role = useSelector((state) => state.auth.user.role);
  const uiLanguage = useSelector((state) => state.settings.config.language);
  const news = useSelector((state) => state.news.data.news);
  const paginatorInfo = useSelector((state) => state.news.paginatorInfo);
  const status = useSelector((state) => state.news.status.general);
  const refresh = useSelector((state) => state.news.refresh);

  // Admin y supervisor pueden crear/editar/eliminar noticias; el resto solo lee.
  const isAdminUser = hasAdminAccess(role);
  const pageTitle = t('news.list', { ns: 'routes' });

  const [view, setView] = useState('grid');
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    dispatch(
      newsSlice.actions.list({
        fields: newsHubFields,
        // Admin ve todos los idiomas (gestiona ambos); cliente/entrenador su idioma.
        filters: isAdminUser ? {} : { language: uiLanguage },
        pagination: { first: PER_PAGE, page },
      })
    );
  }, [dispatch, isAdminUser, uiLanguage, page, refresh]);

  // Al entrar a la lista (montaje) o cambiar de página, volver al inicio del scroll:
  // tras editar una card del fondo, la lista debe abrir desde arriba, no donde estaba.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [page]);

  useEffect(() => {
    setAppBarTitle(pageTitle);
    setAppBarButton(
      isAdminUser ? (
        <AppBarButtonLink label={t('add', { ns: 'buttons', defaultValue: 'Añadir' })} path={routes.createNews.path} />
      ) : null
    );
  }, [isAdminUser, pageTitle, setAppBarButton, setAppBarTitle, t]);

  const total = paginatorInfo?.total || 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));
  const from = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const to = Math.min(page * PER_PAGE, total);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(i18n.language === 'es' ? 'es-CO' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const openArticle = (id) => navigate(routes.viewNews.path.replace(':id', id));

  const confirmDelete = () => {
    if (toDelete) dispatch(newsSlice.actions.remove([toDelete.id]));
    setToDelete(null);
  };

  const renderMeta = (item) => (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <CalendarMonthOutlinedIcon sx={{ fontSize: 15, color: 'gold.main' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        {formatDate(item.created_at)}
      </Typography>
      {isAdminUser && (
        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase' }}>
          · {item.language}
        </Typography>
      )}
    </Stack>
  );

  const renderAdminActions = (item) => (
    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
      <Tooltip title={t('edit', { ns: 'buttons', defaultValue: 'Editar' })}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); navigate(routes.editNews.path.replace(':id', item.id)); }}
          sx={{ backgroundColor: 'rgba(10,10,10,0.65)', color: 'text.primary', '&:hover': { backgroundColor: 'rgba(10,10,10,0.85)', color: 'gold.main' } }}>
          <EditOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setToDelete(item); }}
          sx={{ backgroundColor: 'rgba(10,10,10,0.65)', color: 'text.primary', '&:hover': { backgroundColor: 'rgba(10,10,10,0.85)', color: '#ff8a8a' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const readMore = (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 'auto', pt: 1 }}>
      <Typography variant="body2" sx={{ color: 'gold.main', fontWeight: 800 }}>
        {t('readMore', { ns: 'news', defaultValue: 'Leer más' })}
      </Typography>
      <ArrowForwardIcon sx={{ fontSize: 15, color: 'gold.main' }} />
    </Stack>
  );

  const image = (item, sx) => (
    <Box sx={{ position: 'relative', flexShrink: 0, backgroundColor: 'surface.raised', ...sx }}>
      {isAdminUser && renderAdminActions(item)}
      {(item.featured_image || item.thumbnail) && (
        <Box
          component="img"
          src={item.featured_image || item.thumbnail}
          alt={item.title}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </Box>
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />

      {/* Cabecera del hub: título + contador + toggle grid/lista */}
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography component="h2" sx={{ color: 'text.primary', fontWeight: 800, letterSpacing: '-0.01em', fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
            {pageTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('showingArticles', {
              ns: 'news',
              from,
              to,
              total,
              defaultValue: 'Mostrando {{from}} a {{to}} de {{total}} artículos',
            })}
          </Typography>
        </Box>
        <Stack
          direction="row"
          sx={{ border: '1px solid', borderColor: 'hairline.main', borderRadius: '10px', p: 0.25, display: { xs: 'none', sm: 'flex' } }}>
          <IconButton
            size="small"
            onClick={() => setView('grid')}
            aria-label="vista de cuadrícula"
            sx={{ borderRadius: '8px', color: view === 'grid' ? 'gold.main' : 'text.secondary', backgroundColor: view === 'grid' ? 'rgba(249,189,78,0.12)' : 'transparent' }}>
            <GridViewOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setView('list')}
            aria-label="vista de lista"
            sx={{ borderRadius: '8px', color: view === 'list' ? 'gold.main' : 'text.secondary', backgroundColor: view === 'list' ? 'rgba(249,189,78,0.12)' : 'transparent' }}>
            <ViewListOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <LoadingBlock
        emptyBlockProps={{ message: t('empty', { ns: 'news' }) }}
        isValid={news.length > 0}
        skeletonProps={{ height: 280, rows: 3 }}
        status={status}
        type="news">
        <>
          {view === 'grid' ? (
            <Grid container spacing={3}>
              {news.map((item) => (
                <Grid key={item.id} xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                  <Box
                    onClick={() => openArticle(item.id)}
                    role="button"
                    aria-label={item.title}
                    sx={{ ...cardShellSx, flexDirection: 'column', width: '100%', cursor: 'pointer' }}>
                    {image(item, { aspectRatio: '16 / 9' })}
                    <Stack spacing={1} sx={{ p: 2.25, flex: 1 }}>
                      {renderMeta(item)}
                      <Typography sx={{ color: 'text.primary', fontWeight: 800, lineHeight: 1.3, fontSize: '1.05rem', ...clamp(2) }}>
                        {item.title}
                      </Typography>
                      {item.summary && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', ...clamp(3) }}>
                          {item.summary}
                        </Typography>
                      )}
                      {readMore}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack spacing={2.5}>
              {news.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => openArticle(item.id)}
                  role="button"
                  aria-label={item.title}
                  sx={{ ...cardShellSx, flexDirection: { xs: 'column', sm: 'row' }, cursor: 'pointer' }}>
                  {image(item, { width: { xs: '100%', sm: 230 }, aspectRatio: { xs: '16 / 9', sm: 'auto' } })}
                  <Stack spacing={1} sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
                    {renderMeta(item)}
                    <Typography sx={{ color: 'text.primary', fontWeight: 800, lineHeight: 1.3, fontSize: '1.15rem', ...clamp(2) }}>
                      {item.title}
                    </Typography>
                    {item.summary && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', ...clamp(2) }}>
                        {item.summary}
                      </Typography>
                    )}
                    {readMore}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {lastPage > 1 && (
            <Stack alignItems="center" sx={{ mt: 4 }}>
              <Pagination
                count={lastPage}
                page={page}
                onChange={(_, p) => setPage(p)}
                sx={{
                  '& .MuiPaginationItem-root': { color: 'text.secondary' },
                  '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: 'gold.main', color: '#111418', fontWeight: 800 },
                }}
              />
            </Stack>
          )}
        </>
      </LoadingBlock>

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title={t('deleteNewsTitle', { ns: 'news', defaultValue: 'Eliminar noticia' })}
        message={toDelete ? `"${toDelete.title}"` : ''}
      />
    </>
  );
}

export default NewsHubPage;
